import { useUser, useDomains, useBusinessEmail } from '~/lib/hooks'

export const state = () => ({
  currentSubscription: {},
  subscriptions: [],
  subscription_items: [],
  stripe_subscriptions: [],
  invoices: [],
  stripe_invoices: [],
  payment_methods: [],
  updatingSubscription: null,
})

export const mutations = {
  setCurrentSubscription(state, subscription) {
    state.currentSubscription = subscription
  },

  setSubscriptions(state, subscriptions) {
    state.subscriptions = subscriptions
  },

  setStripeSubscriptions(state, stripe_subscriptions) {
    state.stripe_subscriptions = stripe_subscriptions
  },

  setSubscriptionItems(state, subscription_items) {
    state.subscription_items = subscription_items
  },

  setInvoices(state, invoices) {
    state.invoices = invoices
  },

  setStripeInvoices(state, stripe_invoices) {
    state.stripe_invoices = stripe_invoices
  },

  setPaymentMethods(state, payment_methods) {
    state.payment_methods = payment_methods
  },

  setUpdatingSubscription(state, subscription_id) {
    state.updatingSubscription = subscription_id
  },

  reset(state) {
    state.currentSubscription = {}
  },
}

export const getters = {
  isLiked: state => logo => state.logos.find(({ id }) => id === logo.id),
}

export const actions = {
  /*
   * Get a user's subscriptions
   */
  async fetchSubscriptions({ commit }) {
    // Get user's active subscriptions
    const { data: subscriptionData } = await this.$api.get(
      `/api/v2/subscriptions`,
      {
        params: {
          status: ['active', 'past_due'],
        },
      }
    )

    if (!subscriptionData) {
      return
    }

    const stripe_subscriptions = subscriptionData.subscriptions

    await commit('setStripeSubscriptions', stripe_subscriptions)

    return stripe_subscriptions
  },

  /*
   * Get a user's subscription items
   */
  async fetchSubscriptionItems({ rootState, commit, dispatch }) {
    const stripe_subscriptions = await dispatch('fetchSubscriptions')

    const { getUser } = useUser(this.$api)
    const user = await getUser()

    // Refresh Domains
    const { listDomains } = useDomains(this.$api)
    const { listBusinessEmails } = useBusinessEmail(this.$api)

    const { data } = await listDomains()

    const { data: businessEmails } = await listBusinessEmails()

    await commit('domains/setDomains', data, {
      root: true,
    })

    await commit('business-email/setBusinessEmails', businessEmails, {
      root: true,
    })

    // Refresh Websites
    await dispatch('sites/fetchSites', undefined, {
      root: true,
    })

    // Prepare subscription objects
    const planSubscription = {
      subscription_type: 'plan',
      ...user.plan,
    }
    const domainSubscriptions = rootState.domains.domains.map(domain => ({
      subscription_type: 'domain',
      ...domain,
    }))
    const websiteSubscriptions = rootState.sites.sites.map(site => ({
      subscription_type: 'site',
      ...site,
    }))
    const businessEmailSubscriptions = rootState[
      'business-email'
    ].businessEmails.map(businessEmail => ({
      subscription_type: 'business-email',
      ...businessEmail,
    }))

    // Filter out null subscriptions
    const subscription_items = [
      planSubscription,
      ...domainSubscriptions,
      ...websiteSubscriptions,
      ...businessEmailSubscriptions,
    ].filter(subscription => subscription.subscription_id)

    await commit('setSubscriptionItems', subscription_items)

    // Match subscription items to subscriptions - e.g. groups multiple
    // domains that were purchased in one subscription
    const subscriptions = await Promise.all(
      stripe_subscriptions.map(subscription =>
        dispatch('formatSubscription', {
          subscription,
          subscription_id: subscription.id,
        })
      )
    )

    await commit('setSubscriptions', subscriptions)
  },

  /*
   * Cancel a user's subscription
   */
  async cancelSubscription({ dispatch }, subscription_id) {
    const { data: updatedSubscription } = await this.$api.patch(
      `/api/v2/subscriptions/${subscription_id}/cancel`
    )

    if (!updatedSubscription) {
      return
    }

    await dispatch('replaceSubscription', updatedSubscription)
  },

  /*
   * Reactivate a user's subscription
   */
  async reactivateSubscription({ dispatch }, subscription_id) {
    const { data: updatedSubscription } = await this.$api.patch(
      `/api/v2/subscriptions/${subscription_id}/reactivate`
    )

    if (!updatedSubscription) {
      return
    }

    await dispatch('replaceSubscription', updatedSubscription)
  },

  /*
   * Update or insert a subscription in subscriptions
   */
  async replaceSubscription({ state, commit, dispatch }, subscription) {
    const updatedSubscription = await dispatch('formatSubscription', {
      subscription,
      subscription_id: subscription.id,
    })

    const subscriptionCopy = state.subscriptions

    const indexToUpdate = subscriptionCopy.findIndex(
      sub => sub.id === updatedSubscription.id
    )

    if (indexToUpdate !== -1) {
      subscriptionCopy[indexToUpdate] = updatedSubscription

      await commit('setSubscriptions', [...subscriptionCopy])
    }
  },

  /*
   * Format a endpoint subscription to work in frontend
   */
  formatSubscription({ state }, { subscription, subscription_id }) {
    const items = state.subscription_items.filter(
      item => item.subscription_id === subscription_id
    )

    const firstItem = items[0]

    let title = ''
    let type = ''

    if (firstItem) {
      // Assume first item is accurately represents all items
      switch (firstItem.subscription_type) {
        case 'plan':
          title = firstItem.package.name
          break

        case 'domain':
          title = 'Domain'
          break

        case 'site':
          title = 'Site'
          break

        case 'business-email':
          title = 'Business Email'
          break

        default:
          title = ''
          break
      }

      type = firstItem.subscription_type
    }

    return {
      ...subscription,
      title,
      type,
      items,
    }
  },

  /*
   * Get a user's invoices
   */
  async fetchInvoices({ commit, dispatch }) {
    const { data: invoiceData } = await this.$api.get(
      `/api/v2/subscriptions/invoices`
    )

    if (!invoiceData) {
      return
    }

    const stripeInvoices = invoiceData.invoices
    await commit('setStripeInvoices', stripeInvoices)

    // Match subscription items to invoices
    // e.g. groups multiple domains that were purchased in one invoice
    const invoices = await Promise.all(
      stripeInvoices.map(stripeInvoice =>
        dispatch('formatSubscription', {
          subscription: stripeInvoice,
          subscription_id: stripeInvoice.subscription,
        })
      )
    )

    await commit('setInvoices', invoices)

    return invoices
  },

  /*
   * Get a user's payment methods (cards)
   */
  async fetchPaymentMethods({ commit }) {
    const { data: cardData } = await this.$api.get(
      `/api/v2/subscriptions/cards`
    )

    if (!cardData) {
      return
    }

    const payment_methods = cardData.cards

    await commit('setPaymentMethods', payment_methods)

    return payment_methods
  },

  /*
   * Set a user's default payment method to a given payment method id
   */
  async setDefaultPaymentMethod({ dispatch }, id) {
    const { data: cardData } = await this.$api.patch(
      `/api/v2/subscriptions/cards/default/${id}`
    )

    if (!cardData) {
      return
    }

    const payment_methods = await dispatch('fetchPaymentMethods')

    return payment_methods
  },

  /*
   * Detach a payment method from a customer
   */
  async detachPaymentMethod({ dispatch }, id) {
    const { data: cardData } = await this.$api.delete(
      `/api/v2/subscriptions/cards/detach/${id}`
    )

    if (!cardData) {
      return
    }

    const payment_methods = await dispatch('fetchPaymentMethods')
    await dispatch('fetchSubscriptionItems')

    return payment_methods
  },

  /*
   * Change a subscription's payment method
   */
  async changeSubscriptionPaymentMethod(
    { commit, dispatch },
    { subscription_id, payment_method_id }
  ) {
    await commit('setUpdatingSubscription', subscription_id)
    const { data: updatedSubscription } = await this.$api.patch(
      `/api/v2/subscriptions/${subscription_id}/payment-method/${payment_method_id}`
    )

    if (!updatedSubscription) {
      await commit('setUpdatingSubscription', null)
      return
    }

    await dispatch('replaceSubscription', updatedSubscription)

    await commit('setUpdatingSubscription', null)
  },
}
